import { Alert, Box } from "@mui/material";
import { TableCellType } from "components/LazyTable";
import SortableTable from "components/SortableTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import {
  ListRequestOfSkuProgressionGateFilter,
  ListResponseOfSkuProgressionGate,
} from "services/KnowfullyAdminClient";
import { ProgressionGateStatus } from "types/common-types";
import { SkuProgressionGateWithId } from "types/sku-related-types";
import { getProgressionGateStatus } from "utils/progressionGateUtils";

const SkuProgressionLogic = ({ skuId }: { skuId: string }) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuProgressionGate>(
    ["skuProgressionGates", skuId],
    () =>
      skuService.getSkuProgressionGates(
        skuId,
        ListRequestOfSkuProgressionGateFilter.fromJS({
          sortField: "createdOn",
          sortDescending: true,
        })
      )
  );

  const skuProgressionGatesData = useMemo(
    () => (data?.data ?? []) as SkuProgressionGateWithId[],
    [data?.data]
  );

  const gateStatus = useMemo(
    () => getProgressionGateStatus(skuProgressionGatesData),
    [skuProgressionGatesData]
  );

  const getInfoMessage = () => {
    switch (gateStatus) {
      case ProgressionGateStatus.Deactivated:
        return t({ id: "progressionGateDisabledInfo" });
      case ProgressionGateStatus.Inherited:
        return t({ id: "progressionGateInheritedInfo" });
      case ProgressionGateStatus.Overridden:
        return t({ id: "progressionGateOverriddenInfo" });
      default:
        return null;
    }
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "gateType",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "gateTypeLabel" }),
    },
    {
      accessor: "dialogueTitle",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "dialogueTitleLabel" }),
    },
    {
      accessor: "prompt",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "promptLabel" }),
    },
    {
      accessor: "label",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "placeholderLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "courseProgressionRequiredResponseLabel" }),
    },
    {
      accessor: "errorMessage",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "messageLabel" }),
    },
  ];

  return (
    <>
      {getInfoMessage() && (
        <Box mb={2}>
          <Alert severity="info">{getInfoMessage()}</Alert>
        </Box>
      )}
      {gateStatus !== ProgressionGateStatus.Deactivated && (
        <SortableTable
          {...{
            rows: skuProgressionGatesData,
            loading: isFetching,
            dense: true,
            cells,
            tableRowPerPage: 5,
          }}
        />
      )}
    </>
  );
};

export default withErrorBoundary(SkuProgressionLogic);
